// =============================================================================
// src/lib/realtime/presence.js - private Realtime channels and presence rooms.
//
//   privateChannel(topic, config)
//     supabase.channel(topic, { config: { ...config, private: true } }). Every
//     channel the app opens goes through this (or the room below) so it is
//     authorized by the realtime.messages policies
//     (20270217100000_realtime_authorization) and keeps working once "Allow
//     public access" is turned off in the Realtime settings. A null topic (a
//     missing or malformed id) gets a channel that is never subscribed.
//
//   usePresenceRoom(topic, me) -> { state, selfUid, setExtra }
//     Joins a presence room and returns the raw presence state; setExtra()
//     adds fields to what this tab publishes (the worklist's open item). One Supabase
//     channel per topic is shared by every component that asks for it (the
//     chart header and the drawer, or three worklist cards), because
//     supabase.channel() hands back the SAME channel for a repeated topic and
//     a second .on("presence") after subscribe() would throw.
//
//   useViewers(topic, me)
//     The OTHER staff in a room, one per user, for the avatar row.
//
// Rooms are left on unmount, when the tab has been hidden for two minutes
// (rejoined when it is visible again), and on sign out. Reconnects are left
// to realtime-js (its own backoff); nothing here re-creates a channel on a
// socket error, so a flaky network cannot turn into a reconnect storm.
// =============================================================================
import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "../supabaseClient";
import { PRESENCE_HIDDEN_LEAVE_MS, presencePayload, reducePresence, sameViewers } from "./presenceCore";

const NEVER = { on() { return NEVER; }, subscribe() { return NEVER; }, track() { return Promise.resolve(); }, presenceState() { return {}; }, unsubscribe() { return Promise.resolve(); } };

export function privateChannel(topic, config = {}) {
  // No topic means no id we trust: a channel that never joins is safer than
  // falling back to a public or unscoped topic. supabase.removeChannel()
  // ignores it (it is not in the client's channel list).
  if (!topic) return NEVER;
  return supabase.channel(topic, { config: { ...config, private: true } });
}

// ---- shared rooms ------------------------------------------------------------
const rooms = new Map(); // topic -> room
let globalsReady = false;
let hiddenTimer = null;

function notify(room) {
  let state = {};
  try { state = room.ch ? room.ch.presenceState() || {} : {}; } catch (e) { state = {}; }
  room.state = state;
  room.members.forEach((m) => { try { m.onState(state); } catch (e) { /* one listener failing does not stop the rest */ } });
}

function currentPayload(room) {
  // The most recent member to (re)publish wins: one presence per tab per room.
  let last = null;
  room.members.forEach((m) => { if (m.payload && (!last || m.seq > last.seq)) last = m; });
  return last ? last.payload : null;
}

function open(room) {
  if (room.ch || room.closed) return;
  const ch = privateChannel(room.topic, { presence: { key: room.key } });
  room.ch = ch;
  room.subscribed = false;
  ch.on("presence", { event: "sync" }, () => notify(room));
  ch.subscribe((status) => {
    if (room.ch !== ch) return;
    if (status === "SUBSCRIBED") {
      room.subscribed = true;
      const p = currentPayload(room);
      if (p) ch.track(p).catch(() => { /* realtime-js re-tracks after a rejoin */ });
    } else if (status === "CHANNEL_ERROR" || status === "TIMED_OUT" || status === "CLOSED") {
      room.subscribed = false;
      // Not authorized (another practice, a patient, MFA not satisfied) or the
      // socket dropped: show nobody rather than a stale list.
      notify(room);
    }
  });
}

function close(room) {
  const ch = room.ch;
  room.ch = null;
  room.subscribed = false;
  if (ch && ch !== NEVER) supabase.removeChannel(ch).catch(() => { /* already gone */ });
  notify(room);
}

function installGlobals() {
  if (globalsReady || typeof document === "undefined") return;
  globalsReady = true;
  document.addEventListener("visibilitychange", () => {
    if (document.hidden) {
      clearTimeout(hiddenTimer);
      hiddenTimer = setTimeout(() => {
        rooms.forEach((room) => { room.parked = true; close(room); });
      }, PRESENCE_HIDDEN_LEAVE_MS);
    } else {
      clearTimeout(hiddenTimer);
      hiddenTimer = null;
      rooms.forEach((room) => { if (room.parked) { room.parked = false; open(room); } });
    }
  });
  try {
    supabase.auth.onAuthStateChange((event) => {
      if (event !== "SIGNED_OUT") return;
      // Leave every room at once; components unmount right after, and a room
      // must not outlive the session that joined it.
      rooms.forEach((room) => { room.closed = true; close(room); });
      rooms.clear();
    });
  } catch (e) { /* auth not available (tests) */ }
}

let seq = 0;
function joinRoom(topic, key, member) {
  installGlobals();
  let room = rooms.get(topic);
  if (room && room.key !== key) {
    // A different user in the same tab (sign out then in without unmount):
    // start clean so the old identity is not tracked.
    room.closed = true; close(room); rooms.delete(topic); room = null;
  }
  if (!room) {
    room = { topic, key, ch: null, members: new Set(), state: {}, subscribed: false, parked: false, closed: false };
    rooms.set(topic, room);
  }
  member.seq = ++seq;
  room.members.add(member);
  if (!room.ch && !room.parked) open(room);
  else member.onState(room.state);
  return {
    retrack(payload) {
      member.payload = payload;
      member.seq = ++seq;
      if (room.ch && room.subscribed && payload) room.ch.track(payload).catch(() => { /* resent on rejoin */ });
    },
    leave() {
      room.members.delete(member);
      if (room.members.size === 0) {
        room.closed = true;
        close(room);
        if (rooms.get(topic) === room) rooms.delete(topic);
      } else if (room.ch && room.subscribed) {
        const p = currentPayload(room);
        if (p) room.ch.track(p).catch(() => { /* resent on rejoin */ });
      }
    },
  };
}

// Test hook: how many rooms are open (journeys read it via window).
export function openRoomCount() { return rooms.size; }

export function usePresenceRoom(topic, me) {
  const [state, setState] = useState({});
  const handle = useRef(null);
  // Extra fields (the worklist's open item) change often; they are kept in a
  // ref and re-published without re-rendering the caller.
  const extraRef = useRef(null);
  const base = presencePayload(me);
  const uid = base ? base.uid : null;
  const baseKey = base ? JSON.stringify(base) : "";
  const baseRef = useRef(base);
  baseRef.current = base;
  const build = () => (baseRef.current ? { ...baseRef.current, ...(extraRef.current || {}) } : null);

  useEffect(() => {
    if (!topic || !uid) { setState({}); return undefined; }
    const member = { payload: build(), onState: (s) => setState(s) };
    const h = joinRoom(topic, uid, member);
    handle.current = h;
    return () => { handle.current = null; h.leave(); setState({}); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topic, uid]);

  // Display name or role changed: re-publish on the same channel.
  useEffect(() => {
    if (handle.current) handle.current.retrack(build());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseKey]);

  const setExtra = useCallback((extra) => {
    extraRef.current = extra || null;
    if (handle.current) handle.current.retrack(build());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { state, selfUid: uid, setExtra };
}

export function useViewers(topic, me) {
  const { state, selfUid } = usePresenceRoom(topic, me);
  const [viewers, setViewers] = useState([]);
  useEffect(() => {
    const next = reducePresence(state, selfUid);
    setViewers((prev) => (sameViewers(prev, next) ? prev : next));
  }, [state, selfUid]);
  return viewers;
}
